<template>
  <div
    class="location-card"
    :class="{ 'location-card--active': isActive }"
    @click="handleClick"
  >
    <div class="location-card__header">
      <div class="location-card__title-row">
        <h3 class="location-card__name">
          {{ location.name }}
        </h3>
        <span v-if="isClosest" class="location-card__closest-tag">Closest</span>
        <span v-if="location.distanceText" class="location-card__distance">{{ location.distanceText }}</span>
      </div>
      <div class="location-card__address">
        <span class="location-card__pin-icon" />
        <span>{{ location.address }}</span>
      </div>
      <div class="location-card__parking-tag">
        Free parking • Walk-ins welcome
      </div>
    </div>

    <div v-if="hasHours" class="location-card__meta">
      <div class="location-card__hours">
        <span ref="status" :class="location.isOpen ? 'location-card__status--open' : 'location-card__status--closed'">
          {{ location.isOpen ? 'Open now:' : 'Closed:' }}
        </span>
        <div class="location-card__hours-wrap">
          <div class="location-card__hours-list">
            <span v-if="firstHours" class="location-card__hours-row">
              {{ formatWeekDay(firstHours.weekDay) }}: {{ formatHours(firstHours.businessHours) }}
            </span>
          </div>
          <span
            v-if="canExpand"
            class="location-card__hours-toggle"
            :class="{ 'is-expanded': isActive }"
          />
        </div>
      </div>
    </div>
    <div v-else class="location-card__meta">
      <p class="location-card__hours">
        <span class="location-card__status--closed">Closed</span>
      </p>
    </div>

    <div
      ref="expandable"
      class="location-card__expandable"
      :class="{ 'is-expanded': isActive }"
    >
      <div ref="expandableInner" class="location-card__expandable-inner">
        <div v-if="restHours.length" class="location-card__hours-extra" :style="extraIndentStyle">
          <span
            v-for="(item, idx) in restHours"
            :key="idx"
            class="location-card__hours-row location-card__hours-row--extra"
          >
            {{ formatWeekDay(item.weekDay) }}: {{ formatHours(item.businessHours) }}
          </span>
        </div>
        <div class="location-card__divider" />
        <div class="location-card__details">
          <div class="location-card__detail-item">
            <span class="location-card__icon location-card__icon--phone" />
            <span>{{ location.phone }}</span>
          </div>
          <div class="location-card__detail-item">
            <span class="location-card__icon location-card__icon--email" />
            <span>{{ location.email }}</span>
          </div>
        </div>
        <transition name="fade">
          <span v-if="copyTip" class="location-card__copy-tip">{{ copyTip }}</span>
        </transition>
        <div v-if="mode === 'enroll'" class="location-card__cta-group">
          <button
            class="location-card__cta location-card__cta--normal location-card__cta--full"
            :class="{ 'is-loading': enrollLoading }"
            :disabled="enrollLoading"
            @click.stop="handleEnrollClick"
          >
            <span v-if="enrollLoading" class="location-card__btn-spinner" />
            Enroll Now
          </button>
        </div>
        <div v-else-if="mode === 'select'" class="location-card__cta-group">
          <button
            class="location-card__cta location-card__cta--normal"
            @click.stop="$emit('use-location', location)"
          >
            Use This Location
          </button>
          <a
            class="location-card__cta location-card__cta--secondary location-card__cta--normal"
            :href="centerPageUrl"
            target="_self"
            @click.stop="handleCenterPageClick"
          >
            Go to Center Page
          </a>
        </div>
        <button v-else class="location-card__cta location-card__cta--full" @click.stop="$emit('schedule')">
          Schedule an Assessment
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { getCenterPageUrl } from 'config/centerPageUrl'

export default {
  name: 'LocationCard',
  props: {
    location: {
      type: Object,
      required: true,
    },
    isActive: {
      type: Boolean,
      default: false,
    },
    isClosest: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'schedule',
      validator: (v) => ['schedule', 'select', 'enroll'].includes(v),
    },
    enrollLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      copyTip: '',
      copyTimer: null,
      statusWidth: 0,
    }
  },
  computed: {
    isWindows() {
      if (typeof navigator === 'undefined') return false
      const platform = (navigator.userAgentData && navigator.userAgentData.platform) || navigator.userAgent
      return /Win/i.test(platform)
    },
    hasHours() {
      return Array.isArray(this.location.businessHoursList) && this.location.businessHoursList.length > 0
    },
    canExpand() {
      return this.hasHours && this.location.businessHoursList.length > 1
    },
    firstHours() {
      return this.hasHours ? this.location.businessHoursList[0] : null
    },
    restHours() {
      return this.hasHours ? this.location.businessHoursList.slice(1) : []
    },
    extraIndentStyle() {
      // status 文案宽度 + 与时间列的间距（margin-left: 4px）
      return {
        paddingLeft: `${this.statusWidth + 4}px`,
      }
    },
    centerPageUrl() {
      const loc = this.location
      if (!loc) return '#'
      return getCenterPageUrl(loc.venueId || loc.id)
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.measureStatus()
    })
  },
  beforeDestroy() {
    clearTimeout(this.copyTimer)
  },
  methods: {
    formatWeekDay(value) {
      const map = {
        MONDAY: 'Monday',
        TUESDAY: 'Tuesday',
        WEDNESDAY: 'Wednesday',
        THURSDAY: 'Thursday',
        FRIDAY: 'Friday',
        SATURDAY: 'Saturday',
        SUNDAY: 'Sunday',
      }
      return map[value] || value
    },
    formatHours(businessHours) {
      if (!Array.isArray(businessHours)) return ''
      return businessHours.map((h) => `${h.open} - ${h.close}`).join(' | ')
    },
    measureStatus() {
      const el = this.$refs.status
      if (!el) return
      this.statusWidth = el.getBoundingClientRect().width || 0
    },
    handleClick() {
      this.$emit(this.isActive ? 'deselect' : 'select')
    },
    handleEnrollClick() {
      if (this.enrollLoading) return
      this.$emit('enroll', this.location)
    },
    handleCenterPageClick(event) {
      this.$emit('gotocenterpage', this.location)
      // 中心页 URL 未配置时（'#' 占位），阻止默认锚点跳转避免页面滚到顶部
      if (this.centerPageUrl === '#') {
        event.preventDefault()
      }
    },
    copyEmail() {
      this.copyToClipboard(this.location.email, 'Email copied')
    },
    copyToClipboard(text, tip) {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(() => {
          this.showTip(tip)
        })
      } else {
        const ta = document.createElement('textarea')
        ta.value = text
        ta.style.position = 'fixed'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
        this.showTip(tip)
      }
    },
    showTip(msg) {
      this.copyTip = msg
      clearTimeout(this.copyTimer)
      this.copyTimer = setTimeout(() => {
        this.copyTip = ''
      }, 2000)
    },
  },
}
</script>

<style lang="scss" scoped>
.location-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  padding: 18px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;

  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &--active {
    border-color: var(--txt-color-link);
    box-shadow: 0 8px 6px rgba(0, 0, 0, 0.05);
  }

  &__header {
    // margin-bottom: 2px;
  }

  &__title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;
  }

  &__name {
    font-family: 'Roboto-Bold', sans-serif;
    // font-weight: 700;
    font-size: 20px;
    color: #44454c;
    margin: 0;
    line-height: 27px;
    flex: 1;
  }

  &__closest-tag {
    background: #fff9dd;
    border: 1px solid #ffd200;
    border-radius: 4px;
    padding: 2px 10px;
    font-family: 'Roboto Flex', sans-serif;
    font-size: 14px;
    color: #de9b00;
    line-height: 18px;
    margin-left: 8px;
  }

  &__distance {
    font-family: 'Roboto Flex', sans-serif;
    font-weight: 500;
    font-size: 16px;
    color: #515157;
    margin-left: 10px;
    line-height: 27px;
    letter-spacing: 1px;
  }

  &__address {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-family: 'Roboto Flex', sans-serif;
    font-weight: 500;
    font-size: 18px;
    color: #44454c;
    line-height: 26px;
  }

  &__pin-icon {
    width: 14px;
    height: 19px;
    margin-top: 4px;
    flex-shrink: 0;
    background: url("~assets/images/bm/icon-address-red.webp") 0 0/ 100% auto no-repeat;
    background-size: contain;
  }

  &__meta {
    margin-top: 0;
  }

  &__hours {
    display: flex;
    align-items: flex-start;
    font-family: 'Roboto Flex', sans-serif;
    font-weight: 500;
    font-size: 14px;
    margin: 0;
    line-height: 22px;
  }

  &__status--open {
    color: #28aa00;
  }

  &__status--closed {
    color: #7c7c7c;
  }

  &__hours-wrap {
    display: flex;
    align-items: flex-start;
    flex: 1;
    margin-left: 4px;
    min-width: 0;
  }

  &__hours-list {
    flex: 1;
    color: #7c7c7c;
    font-size: 14px;
    text-transform: capitalize;
  }

  &__hours-row {
    display: block;
    line-height: 22px;

    &--extra {
      color: #7c7c7c;
      text-transform: capitalize;
    }
  }

  &__hours-extra {
    display: flex;
    flex-direction: column;
    font-family: 'Roboto Flex', sans-serif;
    font-weight: 500;
    font-size: 14px;
    color: #7c7c7c;
    margin-bottom: 4px;
  }

  &__expandable {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows 0.3s ease;

    &.is-expanded {
      grid-template-rows: 1fr;
    }
  }

  &__expandable-inner {
    overflow: hidden;
    min-height: 0;
  }

  &__hours-toggle {
    position: relative;
    flex-shrink: 0;
    width: 14px;
    height: 14px;
    margin-top: 4px;
    margin-left: 8px;
    transition: transform 0.3s ease;

    &::before {
      content: '';
      position: absolute;
      top: 3px;
      left: 2px;
      width: 8px;
      height: 8px;
      border-right: 2px solid #7c7c7c;
      border-bottom: 2px solid #7c7c7c;
      transform: rotate(45deg);
      transform-origin: center;
    }

    &.is-expanded {
      transform: rotate(180deg);
    }
  }

  &__divider {
    height: 1px;
    background: #e8e8e8;
    margin: 12px 0;
  }

  &__details {
    margin-bottom: 16px;
  }

  &__detail-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: 'Roboto Flex', sans-serif;
    font-weight: 500;
    font-size: 14px;
    color: #7c7c7c;
    line-height: 27px;
  }

  &__copy-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border: none;
    background: transparent;
    border-radius: 4px;
    cursor: pointer;
    padding: 0;
    flex-shrink: 0;

    &:hover {
      background: #f0f0f0;

      svg {
        stroke: var(--txt-color-link);
      }
    }
  }

  &__copy-tip {
    display: block;
    font-family: 'Roboto Flex', sans-serif;
    font-size: 12px;
    color: #28aa00;
    margin: 4px 0 0;
    line-height: 18px;
  }

  &__icon {
    width: 12px;
    height: 12px;
    flex-shrink: 0;

    &--phone {
      width: 11px;
      height: 11px;
      background: url("~assets/images/bm/icon-tel-red.webp") 0 0/ 100% auto no-repeat;
      background-size: 100% 100%;
    }

    &--email {
      width: 11px;
      height: 9px;
      background: url("~assets/images/bm/icon-mail-red.webp") 0 0/ 100% auto no-repeat;
      background-size: 100% 100%;
    }
  }

  &__parking {
    font-family: 'Roboto Flex', sans-serif;
    font-size: 14px;
    color: #7c7c7c;
    margin: 4px 0 0;
    line-height: 20px;
  }

  &__parking-tag {
    display: inline-block;
    margin-top: 8px;
    font-family: 'Roboto Flex', sans-serif;
    font-weight: 500;
    font-size: 12px;
    line-height: 20px;
    color: #a9a9a9;
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.3s;
  }

  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }

  &__cta-group {
    display: flex;
    gap: 10px;
    width: 100%;
  }

  &__cta {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    min-width: 0;
    height: 40px;
    padding: 0 8px;
    background: var(--txt-color-link);
    border: none;
    border-radius: 6px;
    font-family: 'Roboto Flex', sans-serif;
    font-weight: 600;
    font-size: 16px;
    color: #fff;
    cursor: pointer;
    line-height: 27px;
    text-align: center;
    text-decoration: none;
    transition: background 0.2s, border-color 0.2s, color 0.2s;
    white-space: nowrap;

    &:hover {
      background: var(--txt-color-link);
    }

    &--full {
      width: 100%;
      font-size: 18px;
    }

    &--normal {
      background: var(--txt-color-link);
      border-radius: 10px;
    }

    &--secondary {
      background: #fff;
      color: var(--txt-color-link);
      border: 1px solid var(--txt-color-link);

      &:hover {
        background: var(--txt-color-link);
        color: #fff;
        border-color: var(--txt-color-link);
      }
    }

    &.is-loading,
    &[disabled] {
      cursor: not-allowed;
      opacity: 0.8;

      &:hover {
        background: var(--txt-color-link);
        opacity: 0.8;
      }
    }
  }

  &__btn-spinner {
    display: inline-block;
    width: 14px;
    height: 14px;
    margin-right: 8px;
    border: 2px solid currentColor;
    border-top-color: transparent;
    border-radius: 50%;
    animation: location-card-spin 0.8s linear infinite;
    vertical-align: middle;
  }
}

@keyframes location-card-spin {
  to { transform: rotate(360deg); }
}
</style>
